import { motion, AnimatePresence } from "framer-motion";
import { FaGithub, FaChevronDown } from "react-icons/fa";
import {
  SiTypescript,
  SiNodedotjs,
  SiExpress,
  SiMysql,
  SiSpringboot,
  SiReact,
  SiKotlin,
  SiAndroid,
  SiSwagger,
  SiDotnet,
  SiPostgresql,
} from "react-icons/si";
import { useLang } from '../../context/LangContext'
import "./ProjectsSection.css";
import { useEffect, useState } from 'react';

const techIcons = {
  TypeScript: <SiTypescript title="TypeScript" />,
  "Node.js": <SiNodedotjs title="Node.js" />,
  Express: <SiExpress title="Express" />,
  MySQL: <SiMysql title="MySQL" />,
  "Spring Boot": <SiSpringboot title="Spring Boot" />,
  React: <SiReact title="React" />,
  Kotlin: <SiKotlin title="Kotlin" />,
  Android: <SiAndroid title="Android" />,
  Swagger: <SiSwagger title="Swagger" />,
  ".NET": <SiDotnet title=".NET" />,
  PostgreSQL: <SiPostgresql title="PostgreSQL" />,
};

const categoryColor = {
  "Full-Stack": "#10b981",
  Mobile: "#6366f1",
  API: "#f59e0b",
  "Game/Full-Stack": "#ec4899",
  Frontend: "#38bdf8",
  "Frontend/API": "#a78bfa",
};

const projectStacks = [
  ["TypeScript", "C#", "Node.js", "Express", "MySQL"],
  ["Java", "Spring Boot", "React", "MySQL", "Swagger"],
  ["Kotlin", "Android", "Room", "MVP"],
  ["Java", "Spring Boot"],
  ["React", "Node.js", "Express"],
  ["React", "TypeScript"],
  ["TypeScript"],
];

const projectRepos = [
  "https://github.com/Alan-VSouza/Simplify-sales-management",
  "https://github.com/Alan-VSouza/Projeto-Estacionamento",
  "https://github.com/Alan-VSouza/PersonalTasks",
  "https://github.com/Alan-VSouza/API-REST-Spring",
  "https://github.com/Alan-VSouza/Sudoku-React",
  "https://github.com/Alan-VSouza/todo-react",
  "https://github.com/Alan-VSouza/price-scan-api",
];

export default function ProjectsSection() {
  const { t, lang } = useLang();
  const p = t.projects;
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    setExpanded(null);
  }, [lang]);

  return (
    <section className="projects-section" id="projects">
      <div className="projects-container">
        <span className="projects-code">{p.tag}</span>
        <h1 className="projects-title">
          {p.title} <span className="projects-highlight">.</span>
        </h1>
        <div className="projects-list">
          {p.items.map((proj, idx) => {
            const color = categoryColor[proj.category] ?? "#10b981";
            const isOpen = expanded === idx;
            const stack = projectStacks[idx] || [];
            const repo = projectRepos[idx] || "#";
            return (
              <motion.div
                className={`project-card${isOpen ? " project-card--open" : ""}`}
                key={`${lang}-${idx}`}
                initial={{ opacity: 0, y: 44 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.12 }}
                transition={{ duration: 0.7, delay: idx * 0.07 }}
                style={{ "--card-color": color }}
              >
                <button
                  className="project-header"
                  onClick={() =>
                    setExpanded((prev) => (prev === idx ? null : idx))
                  }
                  aria-expanded={isOpen}
                >
                  <span
                    className="project-category"
                    style={{
                      color,
                      borderColor: color + "55",
                      background: color + "12",
                    }}
                  >
                    {proj.category}
                  </span>
                  <h3 className="project-name">{proj.title}</h3>
                  <FaChevronDown
                    className="project-chevron"
                    style={{
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  />
                </button>

                <div className="project-stack">
                  {stack.map((tech, i) => (
                    <span
                      className="project-badge"
                      key={i}
                      style={{ borderColor: color + "44" }}
                    >
                      {techIcons[tech] && (
                        <span className="project-badge-icon" style={{ color }}>
                          {techIcons[tech]}
                        </span>
                      )}
                      {tech}
                    </span>
                  ))}
                </div>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      className="project-body"
                      key="body"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.32, ease: "easeInOut" }}
                    >
                      <p className="project-desc">{proj.description}</p>
                      <ul className="project-highlights">
                        {proj.highlights.map((h, i) => (
                          <li key={i} style={{ "--bullet-color": color }}>
                            {h}
                          </li>
                        ))}
                      </ul>
                      <div className="project-links">
                        <a
                          href={repo}
                          className="project-link"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ "--link-color": color }}
                        >
                          <FaGithub />
                          {p.github}
                        </a>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
